import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import CourseComments  from '@/components/CourseComments';
import CourseQuestions from '@/components/CourseQuestions';

/**
 * Convert a YouTube watch URL or short URL into an embeddable URL.
 * Returns null if the URL is not recognised as a YouTube link.
 */
function youtubeEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (u.hostname.endsWith('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
      // /embed/<id> URL — already embeddable
      if (u.pathname.startsWith('/embed/')) return url;
    }
  } catch { /* not a valid URL */ }
  return null;
}

function vimeoEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.endsWith('vimeo.com')) {
      // https://vimeo.com/123456 → https://player.vimeo.com/video/123456
      const id = u.pathname.replace(/\//g, '');
      if (id.match(/^\d+$/)) return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* not a valid URL */ }
  return null;
}

function isDirectVideo(url: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

export default function CoursePlayerPage() {
  const { id } = useParams<{ id: string }>();
  const courseId = id ? parseInt(id, 10) : NaN;
  const nav = useNavigate();

  const { isAdmin } = useAuth();

  // Buyers: gate on completed purchase. Admins: skip the purchase query and
  // fetch the course directly so they can preview any video.
  const { data: purchases = [], isLoading: loadingPurchases } =
    trpc.purchases.getUserPurchases.useQuery(undefined, { enabled: !isAdmin });

  const purchase = useMemo(
    () => purchases.find(p => p.courseId === courseId),
    [purchases, courseId],
  );

  const { data: adminCourse, isLoading: loadingAdminCourse } =
    trpc.courses.getById.useQuery(
      { id: courseId },
      { enabled: isAdmin && Number.isFinite(courseId) },
    );

  const [activeSectionId, setActiveSectionId] = useState<number | null>(null);

  // Watched state (yellow dots) for this course's lessons.
  const utils = trpc.useUtils();
  const { data: watchedRows = [] } = trpc.videoViews.listForCourse.useQuery(
    { courseId },
    { enabled: Number.isFinite(courseId) },
  );
  const watchedSet = new Set(watchedRows.filter(r => r.watched).map(r => r.sectionId));

  const setWatched = trpc.videoViews.setWatched.useMutation({
    onSuccess: () => utils.videoViews.listForCourse.invalidate({ courseId }),
  });
  const toggleWatched = (sectionId: number) =>
    setWatched.mutate({ sectionId, watched: !watchedSet.has(sectionId) });

  // Fire a "view recorded" event each time the active section changes
  // (this also marks the lesson watched, filling its dot).
  const recordView = trpc.videoViews.recordView.useMutation({
    onSuccess: () => utils.videoViews.listForCourse.invalidate({ courseId }),
  });
  useEffect(() => {
    if (activeSectionId != null) {
      recordView.mutate({ sectionId: activeSectionId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionId]);

  if (loadingPurchases || loadingAdminCourse) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent
                        rounded-full animate-spin" />
      </div>
    );
  }

  // Buyers without a purchase are locked. Admins are never locked.
  if (!isAdmin && !purchase) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <p className="text-5xl mb-3">🔒</p>
          <h1 className="text-2xl font-bold text-white mb-2">Locked</h1>
          <p className="text-[#71717A] mb-6">
            You haven't purchased this course yet.
          </p>
          <button
            onClick={() => nav('/courses')}
            className="mp-btn mp-btn-primary px-6 py-3"
          >
            Browse Courses
          </button>
        </div>
      </div>
    );
  }

  const course = isAdmin ? adminCourse : purchase!.course;
  if (!course) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent
                        rounded-full animate-spin" />
      </div>
    );
  }
  const sections = course.sections;
  const activeSection =
    sections.find(s => s.id === activeSectionId) ?? sections[0] ?? null;

  // Ask the server for a playable URL.
  //   - For external links (YouTube/Vimeo/MP4) the server returns them as-is.
  //   - For private R2-hosted videos it returns a short-lived signed URL.
  // Either way the player below treats it identically.
  const { data: streamData, isLoading: loadingStream } =
    trpc.videoViews.getStreamUrl.useQuery(
      { sectionId: activeSection?.id ?? 0 },
      { enabled: !!activeSection },
    );
  const videoUrl = streamData?.url ?? null;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 py-10 md:px-6">

        {/* Breadcrumb */}
        <div className="text-sm text-[#52525B] mb-4">
          <Link to="/my-courses" className="hover:text-[#A1A1AA]">My Library</Link>
          <span className="mx-2">/</span>
          <span className="text-[#E4E4E7]">{course.title}</span>
        </div>

        <h1 className="text-3xl font-bold tracking-tight mb-2">{course.title}</h1>
        {course.description && (
          <p className="text-[#A1A1AA] text-base mb-8">{course.description}</p>
        )}

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">

          {/* Player */}
          <div>
            <div
              key={activeSection?.id ?? 'none'}
              className="aspect-video bg-[#09090B] rounded-3xl border border-[#27272A]
                            overflow-hidden flex items-center justify-center">
              {activeSection && loadingStream ? (
                <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent
                                rounded-full animate-spin" />
              ) : activeSection && videoUrl ? (
                (() => {
                  const yt = youtubeEmbed(videoUrl);
                  if (yt) {
                    return (
                      <iframe
                        src={yt}
                        title={activeSection.name}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  }
                  const vm = vimeoEmbed(videoUrl);
                  if (vm) {
                    return (
                      <iframe
                        src={vm}
                        title={activeSection.name}
                        className="w-full h-full"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  }
                  if (isDirectVideo(videoUrl)) {
                    return (
                      <video
                        src={videoUrl}
                        controls
                        className="w-full h-full"
                      />
                    );
                  }
                  return (
                    <div className="text-center p-6">
                      <p className="text-[#71717A] mb-3">
                        Video URL not recognised — open it in a new tab:
                      </p>
                      <a href={videoUrl} target="_blank" rel="noopener noreferrer"
                         className="mp-btn mp-btn-outline px-5 py-2">
                        Open video ↗
                      </a>
                    </div>
                  );
                })()
              ) : (
                <div className="text-center p-6">
                  <p className="text-5xl mb-3">▶</p>
                  <p className="text-[#71717A]">
                    {activeSection
                      ? 'This section does not have a video uploaded yet.'
                      : 'No sections available.'}
                  </p>
                </div>
              )}
            </div>

            {activeSection && (
              <div className="mt-4">
                <h2 className="text-xl font-bold">{activeSection.name}</h2>
                <p className="text-[#71717A] text-sm">{activeSection.durationMinutes} min</p>
              </div>
            )}
          </div>

          {/* Section list */}
          <aside className="bg-[#18181B] border border-[#27272A] rounded-2xl p-4
                            h-fit sticky top-24">
            <h3 className="text-xs font-bold text-[#71717A] uppercase tracking-widest mb-3">
              Course Content
            </h3>
            <ul className="space-y-1">
              {sections.map((s, i) => {
                const isWatched = watchedSet.has(s.id);
                const isActive  = activeSection?.id === s.id;
                return (
                  <li key={s.id} className="flex items-center gap-1">
                    {/* Lesson — click the play icon (or row) to play THIS lesson */}
                    <button
                      onClick={() => setActiveSectionId(s.id)}
                      title={`Play: ${s.name}`}
                      aria-label={`Play ${s.name}`}
                      className={`flex-1 min-w-0 text-left px-3 py-2.5 rounded-xl text-sm
                                  transition-colors flex items-center gap-3
                                  ${isActive
                                    ? 'bg-yellow-400/10 text-yellow-400'
                                    : 'hover:bg-[#27272A] text-[#E4E4E7]'}`}
                    >
                      <span className="w-5 text-xs text-[#52525B] shrink-0">{i + 1}.</span>
                      {/* Play sign */}
                      <span
                        className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center
                                    text-[10px] transition-colors
                                    ${isActive
                                      ? 'bg-yellow-400 text-black'
                                      : 'bg-[#27272A] text-yellow-400 group-hover:bg-yellow-400/20'}`}
                      >
                        ▶
                      </span>
                      <span className="flex-1 truncate">{s.name}</span>
                      <span className="text-[10px] text-[#52525B] shrink-0">{s.durationMinutes}m</span>
                    </button>
                    {/* Watched dot — click to toggle */}
                    <button
                      onClick={() => toggleWatched(s.id)}
                      title={isWatched ? 'Mark as not watched' : 'Mark as watched'}
                      aria-label={isWatched ? 'Mark as not watched' : 'Mark as watched'}
                      className="shrink-0 w-7 h-7 flex items-center justify-center group"
                    >
                      <span className={`w-3 h-3 rounded-full border-2 transition-colors
                                        ${isWatched
                                          ? 'bg-yellow-400 border-yellow-400'
                                          : 'border-[#52525B] group-hover:border-yellow-400/60'}`} />
                    </button>
                  </li>
                );
              })}
              {sections.length === 0 && (
                <p className="text-[#3F3F46] text-xs p-2">No sections yet.</p>
              )}
            </ul>
          </aside>
        </div>

        {/* Q&A — buyers only (this whole page is gated for buyers already) */}
        <CourseQuestions courseId={course.id} />

        {/* Reviews — public; buyer here can post */}
        <CourseComments courseId={course.id} canComment={true} />
      </div>
    </div>
  );
}
